import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import type { TallyState } from '@pedestal-panels/contracts';

/**
 * `<tally-lamp>` — canonical tally-state renderer.
 *
 * The API surface is uniform across vendors: callers set a `TallyState`
 * from `@pedestal-panels/contracts` and the primitive resolves the
 * visual through the active vendor's illumination tokens. The
 * dual-program-iso state is rendered as a vertical split, mirroring
 * physical tally-matrix lamps that light two colours at once — never
 * as a blended colour.
 *
 * See ADR-0004 (Tally architecture).
 */
@customElement('tally-lamp')
export class TallyLampElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        inline-size: 100%;
        block-size: 100%;

        --tl-off: var(--illumination-off, #1a1a1a);
        --tl-program: var(--illumination-program-on, #cc1f1f);
        --tl-preview: var(--illumination-preview-on, #0f9d58);
        --tl-iso: var(--illumination-iso-on, #e8b015);
        --tl-comms: var(--illumination-function-on, #c89a14);
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }
    `,
  ];

  /** Active tally state. */
  @property({ type: String, reflect: true }) accessor state: TallyState = 'off';

  /**
   * Whether the host renders a circular lamp (LED dot) or a stripe (LED
   * bar). Defaults to circle for round tally lamps; stripe for thin
   * horizontal LEDs above an alphanumeric display.
   */
  @property({ type: String, reflect: true }) accessor shape: 'circle' | 'stripe' = 'circle';

  #colourFor(state: TallyState): string {
    switch (state) {
      case 'program':
        return 'var(--tl-program)';
      case 'preview':
        return 'var(--tl-preview)';
      case 'iso':
        return 'var(--tl-iso)';
      case 'comms':
        return 'var(--tl-comms)';
      case 'custom':
        return 'var(--tl-program)';
      case 'off':
      case 'dual-pgm-iso':
      default:
        return 'var(--tl-off)';
    }
  }

  #renderCircle() {
    if (this.state === 'dual-pgm-iso') {
      return html`
        <svg viewBox="0 0 40 40">
          <defs>
            <radialGradient id="tl-pgm-glow" cx="0.3" cy="0.5" r="0.5">
              <stop offset="0.7" stop-color="var(--tl-program)" stop-opacity="0.9" />
              <stop offset="1" stop-color="var(--tl-program)" stop-opacity="0.1" />
            </radialGradient>
            <radialGradient id="tl-iso-glow" cx="0.7" cy="0.5" r="0.5">
              <stop offset="0.7" stop-color="var(--tl-iso)" stop-opacity="0.9" />
              <stop offset="1" stop-color="var(--tl-iso)" stop-opacity="0.1" />
            </radialGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="var(--tl-off)" />
          <path d="M 20 2 A 18 18 0 0 0 20 38 Z" fill="url(#tl-pgm-glow)" />
          <path d="M 20 2 A 18 18 0 0 1 20 38 Z" fill="url(#tl-iso-glow)" />
        </svg>
      `;
    }

    const fill = this.#colourFor(this.state);
    const glow = this.state === 'off' ? '0' : '0.6';
    return html`
      <svg viewBox="0 0 40 40">
        <defs>
          <radialGradient id="tl-glow-${this.state}" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.4" stop-color=${fill} stop-opacity="1" />
            <stop offset="1" stop-color=${fill} stop-opacity="0" />
          </radialGradient>
        </defs>
        <circle cx="20" cy="20" r="19" fill=${fill} opacity=${glow} filter="blur(1.5px)" />
        <circle cx="20" cy="20" r="14" fill=${fill} stroke="#0a0a0a" stroke-width="0.6" />
      </svg>
    `;
  }

  #renderStripe() {
    // The base colour for a stripe-style tally is the LED's own
    // colour — set per-element by the composition through --pb-led
    // (which the OCP-400 wiring fills from the inventory's
    // illumination.colours[0].hex). This lets the four tally bars at
    // the top of an OCP-400 carry their own assignments — yellow on
    // the outer edges (ISO / recording), red flanking the CAM-ID
    // readout (on air) — even though the primitive accepts a single
    // generic TallyState.
    //
    // The lit state borrows the layered-bloom convention from the
    // dot-matrix readouts (sharp inner core → hot mid-glow → soft
    // outer halo) so the four tally LEDs read as a coordinated
    // emissive family with the cam-id and iris-fnum displays — same
    // physical-LED-on-chassis idiom, different colours per assignment.
    const base = 'var(--pb-led, var(--tl-program))';
    const isOff = this.state === 'off';
    const dim = `color-mix(in oklab, ${base} 38%, color-mix(in oklab, ${base} 25%, #1c140e))`;
    const surface = isOff ? dim : base;
    const opacity = isOff ? '0.92' : '1';
    return html`
      <div
        style="
          inline-size: 100%;
          block-size: 100%;
          background: linear-gradient(
            180deg,
            color-mix(in oklab, ${surface} 88%, white) 0%,
            ${surface} 45%,
            color-mix(in oklab, ${surface} 85%, black) 100%
          );
          opacity: ${opacity};
          border: 0.5px solid color-mix(in oklab, ${base} 55%, black);
          border-radius: 12%;
          box-shadow:
            inset 0 0.6px 0 color-mix(in oklab, ${surface} 60%, white),
            inset 0 -0.6px 0 color-mix(in oklab, ${surface} 70%, black),
            ${isOff
              ? '0 0 0.04rem rgba(0, 0, 0, 0.4)'
              : `0 0 0.04rem #ffffff,
                 0 0 0.08rem ${base},
                 0 0 0.22rem color-mix(in oklab, ${base} 75%, transparent),
                 0 0 0.5rem color-mix(in oklab, ${base} 40%, transparent)`};
        "
      ></div>
    `;
  }

  override render() {
    return this.shape === 'stripe' ? this.#renderStripe() : this.#renderCircle();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tally-lamp': TallyLampElement;
  }
}
